// src/components/Filter/FilterLevel2/FilterPanelLevel2.tsx

import "./FilterPanelLevel2.css";
import {
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";

const severityOptions = [
  "Negligible",
  "Low",
  "Medium",
  "High",
  "Critical",
  "Unknown",
];

export type Filters = {
  Severity: string[];
  PackageName: string;
  Layer: number | "";
};

type Props = {
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  packageOptions?: string[];
  layerOptions?: number[];
  onPackagesOpen?: () => void;
};

function Help({ id, tip, label }: { id: string; tip: string; label: string }) {
  return (
    <button
      type="button"
      className="fp-help"
      aria-label={`What is "${label}"?`}
      aria-describedby={id}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      ?
      <span id={id} role="tooltip" className="fp-help-tip">
        {tip}
      </span>
    </button>
  );
}

function TitleWithHelp({
  children,
  id,
  tip,
}: {
  children: React.ReactNode;
  id: string;
  tip: string;
}) {
  return (
    <div className="fp-rowtitle">
      <Typography gutterBottom className="fp-rowtitle-text">
        {children}
      </Typography>
      <Help id={id} tip={tip} label={String(children)} />
    </div>
  );
}

export default function FilterPanelLevel2({
  filters,
  setFilters,
  packageOptions = [],
  layerOptions = [],
  onPackagesOpen,
}: Props) {
  const toggleSeverity = (level: string) => {
    setFilters((prev) => {
      const cur = new Set(prev.Severity);
      cur.has(level) ? cur.delete(level) : cur.add(level);
      return { ...prev, Severity: Array.from(cur) };
    });
  };

  const handlePackageChange = (value: string) => {
    setFilters((prev) => ({ ...prev, PackageName: value }));
  };

  const handleLayerChange = (value: string) => {
    if (value === "") {
      setFilters((prev) => ({ ...prev, Layer: "" }));
    } else {
      const n = Number(value);
      setFilters((prev) => ({ ...prev, Layer: Number.isFinite(n) ? n : "" }));
    }
  };

  const clearAll = () =>
    setFilters({
      Severity: [],
      PackageName: "",
      Layer: "",
    });

  return (
    <div className="filterpanel-lvl2 filter-panel-l2">
      <div className="filter-inner-l2">
        <div className="filter-header-l2">
          <span className="filter-header-title-l2">Filters</span>
        </div>

        {/* Severity */}
        <div className="filter-section-l2">
          <TitleWithHelp
            id="fp2-severity"
            tip="Filter vulnerabilities by severity level."
          >
            Severity
          </TitleWithHelp>
          <FormGroup className="fp2-severity-grid">
            {severityOptions.map((level) => (
              <FormControlLabel
                key={level}
                control={
                  <Checkbox
                    size="small"
                    checked={filters.Severity.includes(level)}
                    onChange={() => toggleSeverity(level)}
                  />
                }
                label={level}
              />
            ))}
          </FormGroup>
        </div>

        {/* Package & Layer */}
        <div className="filter-section-l2">
          <TitleWithHelp
            id="fp2-package-layer"
            tip="Narrow vulnerabilities to a specific package and/or image layer."
          >
            Package & Layer
          </TitleWithHelp>

          <div className="fp2-row">
            <label className="fp2-label" htmlFor="fp2-package">
              Package
            </label>
            <select
              id="fp2-package"
              className="fp2-select"
              value={filters.PackageName}
              onChange={(e) => handlePackageChange(e.target.value)}
              onClick={onPackagesOpen}
            >
              <option value="">All packages</option>
              {packageOptions.map((pkg) => (
                <option key={pkg} value={pkg}>
                  {pkg}
                </option>
              ))}
            </select>
          </div>

          <div className="fp2-row">
            <label className="fp2-label" htmlFor="fp2-layer">
              Layer
            </label>
            <select
              id="fp2-layer"
              className="fp2-select fp2-select-small"
              value={filters.Layer === "" ? "" : String(filters.Layer)}
              onChange={(e) => handleLayerChange(e.target.value)}
            >
              <option value="">All layers</option>
              {layerOptions.map((layer) => (
                <option key={layer} value={layer}>
                  {layer}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Clear button */}
        <div className="filter-clear-row-l2">
          <Button
            variant="contained"
            color="error"
            onClick={clearAll}
            className="filter-clear-btn-l2"
            aria-label="Clear filters"
            title="Clear all filters"
          >
            <FontAwesomeIcon icon={faTrashCan} />
          </Button>
        </div>
      </div>
    </div>
  );
}
